import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import CatalogRequestModal from "@/components/CatalogRequestModal";
import SaleCountdown from "@/components/SaleCountdown";

const COUNTDOWN = {
  legacy: { date: "2026-12-05T13:00:00-07:00", eyebrow: "Legacy of the Big Sky", sublabel: "Dec 5  •  Billings, MT" },
  annual: { date: "2027-02-15T13:00:00-07:00", eyebrow: "Bulls of the Big Sky", sublabel: "Feb 15  •  Billings, MT" },
};

const AUCTION_PHOTO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_import-hub-80/artifacts/55p3x4ic_20240219_130437.webp";

const SALE_GALLERY = {
  legacy: [
    { src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_import-hub-80/artifacts/c22v328l_BBS_213_7598.webp", alt: "Red Angus bull on straw" },
    { src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_import-hub-80/artifacts/z1kz5skd_BBS_68_6674.webp", alt: "Black baldy bull on straw" },
    { src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_import-hub-80/artifacts/hwvr4ro1_BBS_185_6101.webp", alt: "Black bull on straw" },
  ],
  annual: [
    { src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_import-hub-80/artifacts/3sd0pgpy_BBS_38_7162.webp", alt: "Black bull at the pens" },
    { src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_import-hub-80/artifacts/g1ybvpe0_BBS_34_7273.webp", alt: "Black bull on straw" },
  ],
};

const DEVELOPMENT = {
  annual: {
    facility: "Veltkamp Livestock Feedlot — Manhattan, MT",
    address: "11526 Gravel Pit Rd  •  3 miles south off the I-90 exit",
    body: "The bulls start their development ration around October 1st, when they are sorted by weight. They come off test around the end of January. BVD tested, fully vaccinated — and every bull sifted by a non-member panel for structure, performance and disposition.",
  },
  legacy: {
    facility: "Fauth Ranch Feedlot — Lavina, MT",
    address: "2 Fauth Ln  •  11.5 miles NE of Lavina",
    body: "Spring-born calves are in the development yard by October 1st; fall-born calves are started in April — developed on a high-roughage diet for slow, steady growth. BVD tested, fully vaccinated — and every bull sifted by a non-member panel for structure, performance and disposition.",
  },
};

const HERO_POSITION = { legacy: "center 92%", annual: "center 75%" };

const ABOUT = {
  legacy: {
    heading: "The Inaugural Bulls of the Big Sky Legacy Sale",
    counts: "80 Age-Advantaged Bulls  •  100 Commercial Bred Heifers",
    body: "Join us for the inaugural Legacy Sale, held in conjunction with the Western Choice Female Sale at Billings Livestock Commission.",
    dateline: "December 5, 2026  •  1:00 PM MT",
    place: "Billings Livestock Commission  •  Billings, Montana",
    note: "Catalog and lot videos coming soon.",
  },
  annual: {
    heading: "The 18th Annual Bulls of the Big Sky Sale",
    counts: "200+ Yearling Bulls from Eight Programs",
    body: "Join us for the cornerstone of the sale season.",
    dateline: "February 15, 2027  •  1:00 PM MT",
    place: "Billings Livestock Commission  •  Billings, Montana",
    note: "Catalog and lot videos coming soon.",
  },
};

export default function SalePage() {
  const { slug } = useParams();
  const [sale, setSale] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get(`/sales/${slug}`),
          api.get("/site-settings"),
        ]);
        setSale(s.data);
        setSettings(st.data);
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="text-center">
          <div className="font-display text-6xl mb-4">Sale not found</div>
          <Link to="/" className="link-brass">Return home →</Link>
        </div>
      </div>
    );
  }
  if (!sale) return <div data-testid="sale-loading" className="min-h-screen" style={{ background: "var(--bg-primary)" }} />;

  return (
    <div data-testid={`sale-page-${slug}`} className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      <SiteHeader />
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={sale.hero_image_url} alt={sale.name} className="w-full h-full object-cover" style={{ objectPosition: HERO_POSITION[slug] || "center top" }} />
        </div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-20 pt-32">
          <div className="eyebrow reveal" style={{ color: "#E8D7A8" }}>{sale.tagline}</div>
          {slug === "legacy" || slug === "annual" ? (
            <h1
              data-testid="sale-title"
              className="font-display mt-5 reveal reveal-delay-1 leading-[0.9]"
              style={{ color: "#FFFFFF", textShadow: "0 4px 28px rgba(14,44,70,0.65), 0 2px 6px rgba(14,44,70,0.5)", fontWeight: 800 }}
            >
              <span className="block text-6xl md:text-8xl lg:text-9xl tracking-wide">
                {slug === "legacy" ? "LEGACY" : "BULLS"}
                <span
                  className="font-italic-serif text-3xl md:text-5xl lg:text-6xl mx-3 md:mx-5 align-middle"
                  style={{ color: "#E8D7A8", fontWeight: 500, textTransform: "none", letterSpacing: "0.02em" }}
                >
                  of the
                </span>
                BIG SKY
              </span>
            </h1>
          ) : (
            <h1
              data-testid="sale-title"
              className="font-display text-6xl md:text-8xl lg:text-9xl mt-5 reveal reveal-delay-1"
              style={{ color: "#FFFFFF", textShadow: "0 4px 24px rgba(14,44,70,0.55)" }}
            >
              {sale.name}
            </h1>
          )}
          <div className="font-italic-serif text-3xl md:text-5xl mt-4 reveal reveal-delay-2" style={{ color: "#E8D7A8" }}>
            {sale.date_label}
          </div>
          <div className="mt-2 text-base" style={{ color: "rgba(255,255,255,0.85)" }}>{sale.location}</div>
          {COUNTDOWN[slug] && <SaleCountdown {...COUNTDOWN[slug]} />}
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="eyebrow mb-4">About the Sale</div>
            {ABOUT[slug] ? (
              <div data-testid="sale-about-block">
                <h2 className="font-display text-3xl md:text-4xl leading-[1.1]" style={{ color: "var(--text-primary)" }}>
                  {ABOUT[slug].heading}
                </h2>
                <div className="font-display text-xl md:text-2xl mt-4" style={{ color: "var(--brass-deep)" }}>
                  {ABOUT[slug].counts}
                </div>
                <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--text-secondary)" }}>
                  {ABOUT[slug].body}
                </p>
                <div className="mt-6 font-bold text-base md:text-lg" style={{ color: "var(--text-primary)" }}>
                  {ABOUT[slug].dateline}
                  <div className="font-bold">{ABOUT[slug].place}</div>
                </div>
                <p className="mt-4 text-base" style={{ color: "var(--text-muted)" }}>{ABOUT[slug].note}</p>
              </div>
            ) : (
              <p className="font-display text-3xl md:text-4xl leading-[1.15]" style={{ color: "var(--text-primary)" }}>
                {sale.description}
              </p>
            )}

            <div className="mt-12 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              {sale.catalog_url ? (
                <a href={sale.catalog_url} target="_blank" rel="noopener noreferrer" data-testid="sale-catalog-btn" className="btn-primary">
                  Sale Catalog (PDF) →
                </a>
              ) : (
                <span className="btn-ghost opacity-60 cursor-not-allowed">Catalog coming soon</span>
              )}
              <button
                type="button"
                data-testid="sale-request-catalog"
                onClick={() => setCatalogOpen(true)}
                className="btn-ghost"
              >
                Request a Printed Catalog
              </button>
              {slug === "legacy" && (
                <Link
                  to="/buyer-info/legacy"
                  data-testid="sale-buyer-terms-btn"
                  className="btn-ghost"
                >
                  Buyer Info & Sale Terms →
                </Link>
              )}
              {sale.past_sale_url && (
                <a
                  href={sale.past_sale_url}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="sale-past-link"
                  className="btn-ghost"
                >
                  {sale.past_sale_label || "Past Sale Archive"} →
                </a>
              )}
              {sale.past_catalog_url && (
                <a
                  href={sale.past_catalog_url}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="sale-past-catalog-btn"
                  className="btn-ghost"
                >
                  {sale.past_catalog_label || "Past Sale Catalog (PDF)"} →
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="border p-8 md:p-10" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-secondary)" }}>
              <div className="eyebrow mb-6">Schedule</div>
              <ol className="space-y-6">
                {(sale.schedule || []).map((s) => (
                  <li key={`${s.time}-${s.label}`} className="border-l-2 pl-5" style={{ borderColor: "rgba(30,77,117,0.4)" }}>
                    <div className="font-display text-xl" style={{ color: "var(--brass)" }}>{s.time}</div>
                    <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>{s.label}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* HOW THE BULLS ARE DEVELOPED */}
      {DEVELOPMENT[slug] && (
        <section data-testid="sale-development" className="px-6 md:px-12 pb-20 md:pb-24">
          <div className="max-w-[1400px] mx-auto border-t pt-16" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
              <div className="md:col-span-5">
                <div className="eyebrow mb-4">How the Bulls Are Developed</div>
                <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">
                  Ready to<br />go to work.
                </h2>
                <div className="mt-8 border p-6" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-secondary)" }}>
                  <div className="eyebrow text-[10px]">Development Facility</div>
                  <div className="font-display text-2xl mt-2 leading-tight">{DEVELOPMENT[slug].facility}</div>
                  <div className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{DEVELOPMENT[slug].address}</div>
                </div>
              </div>
              <div className="md:col-span-7 md:pl-8 md:border-l" style={{ borderColor: "var(--border-subtle)" }}>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  {DEVELOPMENT[slug].body}
                </p>
                <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  A high-forage ration fed for a 3 lb. daily gain — not pushed, not fattened, developed
                  to last. <span className="font-italic-serif" style={{ color: "var(--brass-deep)" }}>These bulls will be athletic and ready to work.</span>
                </p>
                <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  Every bull is disposition scored, and you&apos;ll find RightChoice accreditations
                  throughout the catalog — <strong>ACE</strong> (calving ease), <strong>G+</strong>{" "}
                  (balanced genetic value) and <strong>ATM</strong> (terminal) — to help match a bull
                  to your needs.{" "}
                  <Link to="/accreditation" className="link-brass" data-testid="dev-accreditation-link">
                    About the accreditations →
                  </Link>
                </p>
                <div className="mt-6 flex items-center gap-4" data-testid="dev-accreditation-logos">
                  <img src="/images/AccreditedCalvingEase_LOGOweb.jpg" alt="ACE — Accredited Calving Ease" className="h-16 w-16 object-contain" />
                  <img src="/images/AccreditedGenomicPlus_LOGOweb.jpg" alt="G+ — Accredited Genomic Plus" className="h-16 w-16 object-contain" />
                  <img src="/images/AccreditedTerminalMate_LOGOweb.jpg" alt="ATM — Accredited Terminal Mate" className="h-16 w-16 object-contain" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FILLER GALLERY — the kind that sell here */}
      {(SALE_GALLERY[slug] || []).length > 0 && (
        <section data-testid="sale-gallery" className="px-6 md:px-12 pb-20 md:pb-24">
          <div className="max-w-[1400px] mx-auto">
            <div className="eyebrow mb-3">The Kind That Sell Here</div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${SALE_GALLERY[slug].length > 2 ? "lg:grid-cols-3" : ""} gap-6 mt-6`}>
              {SALE_GALLERY[slug].map((g) => (
                <div key={g.src} className="overflow-hidden border" style={{ borderColor: "var(--border-subtle)" }}>
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    className="w-full aspect-[3/2] object-cover transition-transform duration-500 hover:scale-[1.03]"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HEIFERS — legacy only, a sale first */}
      {slug === "legacy" && (
        <section data-testid="legacy-heifers-section" className="px-6 md:px-12 pb-20 md:pb-24">
          <div className="max-w-[1400px] mx-auto border-t pt-16" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
              <div className="md:col-span-5">
                <div className="eyebrow mb-4">A Legacy Sale First</div>
                <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">
                  100 commercial<br />bred heifers.
                </h2>
              </div>
              <div className="md:col-span-7 md:pl-8 md:border-l" style={{ borderColor: "var(--border-subtle)" }}>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  For the first time, females sell at the Legacy Sale. 100 commercial bred heifers —
                  offered in conjunction with the Western Choice Female Sale at Billings Livestock
                  Commission, same day as the bulls.
                </p>
                <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>
                  These heifers come from the same dedicated breeders who stand behind every bull in
                  this sale.{" "}
                  <span className="font-italic-serif" style={{ color: "var(--brass-deep)" }}>
                    Quality genetics, backed by years of experience.
                  </span>
                </p>
                <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  Full breeding details will post here as they&apos;re confirmed. Questions in the
                  meantime? <Link to="/buyer-info/legacy" className="link-brass" data-testid="heifers-buyer-info-link">See Legacy buyer info →</Link>
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* OFFICIAL FLYER — legacy only */}
      {slug === "legacy" && (
        <section data-testid="legacy-flyer-section" className="px-6 md:px-12 pb-20 md:pb-24">
          <div className="max-w-[1400px] mx-auto border-t pt-16" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
              <div className="md:col-span-5">
                <div className="eyebrow mb-4">Sale Day, All On One Page</div>
                <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">
                  The official<br />sale flyer.
                </h2>
                <p className="mt-6 text-base md:text-lg leading-relaxed max-w-md" style={{ color: "var(--text-secondary)" }}>
                  Western Choice Female Sale at noon, Legacy bulls and bred heifers to follow —
                  schedule, headquarters hotel and contacts, all on one page. Tap to open or save it.
                </p>
              </div>
              <div className="md:col-span-7 flex md:justify-end">
                <a
                  href="/images/legacy-sale-flyer.jpg"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="legacy-flyer-image-link"
                  className="frame-image block transition-transform duration-300 hover:scale-[1.01]"
                  style={{ maxWidth: "440px" }}
                >
                  <img
                    src="/images/legacy-sale-flyer.jpg"
                    alt="Western Choice & Legacy of the Big Sky official sale flyer — December 5, 2026"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SALE DAY BAND — annual only */}
      {slug === "annual" && (
        <section data-testid="sale-day-band" className="relative overflow-hidden">
          <img
            src={AUCTION_PHOTO}
            alt="Buyers filling the seats around the auction ring"
            loading="lazy"
            className="w-full object-cover"
            style={{ maxHeight: 620, objectPosition: "center 45%" }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, rgba(14,44,70,0.75) 0%, rgba(14,44,70,0.15) 40%, rgba(14,44,70,0) 65%)" }}
          />
          <div className="absolute bottom-0 left-0 right-0 max-w-[1400px] mx-auto px-6 md:px-12 pb-8 md:pb-10">
            <div className="eyebrow" style={{ color: "#E8D7A8" }}>Sale Day</div>
            <div className="font-display text-3xl md:text-4xl mt-1" style={{ color: "#FFFFFF", textShadow: "0 2px 16px rgba(14,44,70,0.6)" }}>
              A full house, every February.
            </div>
          </div>
        </section>
      )}

      {/* PAST SALE RECAP — shows when admin has filled past_sale_url */}
      {sale.past_sale_url && (
        <section data-testid="past-sale-section" className="px-6 md:px-12 pb-24 md:pb-28">
          <div className="max-w-[1400px] mx-auto border" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-secondary)" }}>
            <div className="p-8 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <div className="eyebrow mb-3">From the Archive</div>
                <h3 className="font-display text-4xl md:text-5xl leading-[1.05]">
                  {sale.past_sale_label || "Past Sale  •  Lots & Results"}
                </h3>
                <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
                  Browse every lot from our most recent sale on DV Auction — lot videos, pedigrees, EPDs,
                  and final results. Useful when you&apos;re tracking a bloodline, evaluating a consignor,
                  or scouting what to look for in the next catalog.
                </p>
              </div>
              <div className="md:col-span-5 md:flex md:flex-col md:items-end gap-3 flex flex-col items-start">
                <a
                  href={sale.past_sale_url}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="past-sale-link"
                  className="btn-primary"
                >
                  View on DV Auction →
                </a>
                {sale.past_catalog_url && (
                  <a
                    href={sale.past_catalog_url}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="past-catalog-link"
                    className="btn-ghost"
                  >
                    {sale.past_catalog_label || "2026 Sale Catalog (PDF)"} →
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      <SiteFooter settings={settings} />
      <CatalogRequestModal open={catalogOpen} onClose={() => setCatalogOpen(false)} />
    </div>
  );
}
